import { BaseNav, buildNavItemFromRoute, buildRoute, RouteError, useNavItemStore } from '@cksoftware/react-base';
import { RouteObject } from 'react-router-dom';
import {
  ForgotPasswordRouteItem,
  LoginRouteItem,
  LogoutRouteItem,
  MyAccountRouteItem,
  RegisterRouteItem,
  ResetPasswordRouteItem
} from './AccountRoutes';
import { AdminNavItems, AdminRouteItem } from './AdminRoutes';
import { ApplicationRoutes } from '../constants/appRoutes';
import { ClientLayout } from '../components/layout/clientLayout';
import {
  AboutUsRouteItem,
  AuctionLotReportRouteItem,
  AuctionReportRouteItem,
  BuyerApprovalRouteItem,
  ContactUsRouteItem,
  HomeRouteItem,
  ListingMarketsRouteItem,
  MarketReportDetailRouteItem,
  MarketReportsRouteItem,
  SupportAndDemoRouteItem,
  TermsOfServiceRouteItem
} from './ClientRoutes';
import { HomePage } from '../features/home/HomePage';
import { FlexLayout } from '../components/layout/flexLayout';
import { StreamingNavItems } from './StreamingRoutes.tsx';
import { ConsignerRoutes } from './ConsignerRoutes.tsx';
import { ManagePurebredBuyersRoutes } from './ManagePurebredBuyersRoutes.tsx';
import { ManageAuctionRoutes } from './ManageAuctionRoutes.tsx';
import { ManageLiveSaleRoutes } from './ManageLiveSaleRoutes.tsx';

const UnauthenticatedRouteItems = [
  LoginRouteItem,
  ForgotPasswordRouteItem,
  RegisterRouteItem,
  TermsOfServiceRouteItem,
  ResetPasswordRouteItem
];

export const ClientRouteItems = [
  HomeRouteItem,
  AboutUsRouteItem,
  ContactUsRouteItem,
  ListingMarketsRouteItem,
  MarketReportsRouteItem,
  BuyerApprovalRouteItem,
  SupportAndDemoRouteItem,
  AuctionLotReportRouteItem,
  AuctionReportRouteItem,
  MarketReportDetailRouteItem
];

export const AdminRouteItems = [
  AdminNavItems,
  ManageAuctionRoutes,
  ManagePurebredBuyersRoutes,
  ManageLiveSaleRoutes,
  ConsignerRoutes,
  StreamingNavItems
];

const AuthenticatedRouteItems = [MyAccountRouteItem, LogoutRouteItem, ...AdminRouteItems];

export function useNavWithRouting() {
  const setNavItemStore = useNavItemStore((state) => state.set);
  const routes = new Array<RouteObject>();
  const nav: BaseNav.NavItem[] = [];

  routes.push({
    path: '/',
    element: <ClientLayout />,
    errorElement: <RouteError />,
    children: [
      {
        path: '/',
        element: <HomePage />,
        errorElement: <RouteError />
      }
    ]
  });

  routes.push(buildRoute(AdminRouteItem));
  ClientRouteItems.forEach((value) => {
    routes.push(buildRoute(value));
  });

  AuthenticatedRouteItems.forEach((value) => {
    routes.push(buildRoute(value));
    const navItem = buildNavItemFromRoute(value);
    if (navItem) {
      nav.push(navItem);
    }
  });

  UnauthenticatedRouteItems.forEach((value) => {
    routes.push(buildRoute(value));
    const navItem = buildNavItemFromRoute(value);
    if (navItem) {
      nav.push(navItem);
    }
  });

  setNavItemStore(nav);

  routes.push({
    path: `/live-auction`,
    element: <FlexLayout containerProps={{ w: '100%' }} />,
    errorElement: <RouteError />,
    children: [
      {
        path: `${ApplicationRoutes.liveAuction.auctioneer}/:auctionId`,
        async lazy() {
          const component = await import('../features/liveAuction/AuctioneerProvider');
          return { Component: component.AuctioneerProvider };
        }
      },
      {
        path: `${ApplicationRoutes.liveAuction.bidder}/:auctionId`,
        async lazy() {
          const component = await import('../features/liveAuction/BidderProvider.tsx');
          return { Component: component.BidderProvider };
        }
      },
      {
        path: ApplicationRoutes.liveAuction.event,
        async lazy() {
          const component = await import('../features/liveAuction/EventProvider.tsx');
          return { Component: component.EventProvider };
        }
      }
    ]
  });

  // Bid Boards Routes
  routes.push({
    path: '/bid-boards',
    element: <FlexLayout containerProps={{ w: '100%', h: '100vh' }} />,
    errorElement: <RouteError />,
    children: [
      {
        path: ApplicationRoutes.bidBoards.auctioneer,
        async lazy() {
          const component = await import('../features/bidBoards/components/AuctioneerBidScreen');
          return { Component: component.AuctioneerBidScreen };
        }
      },
      {
        path: ApplicationRoutes.bidBoards.active,
        async lazy() {
          const component = await import('../features/bidBoards/components/ActiveBidDisplay');
          return { Component: component.ActiveBidDisplay };
        }
      },
      {
        path: ApplicationRoutes.bidBoards.asking,
        async lazy() {
          const component = await import('../features/bidBoards/components/AskingBidDisplay');
          return { Component: component.AskingBidDisplay };
        }
      },
      {
        path: ApplicationRoutes.bidBoards.presortSold,
        async lazy() {
          const component = await import('../features/bidBoards/components/PresortSoldBoard');
          return { Component: component.PresortSoldBoard };
        }
      },
      {
        path: ApplicationRoutes.bidBoards.presortSelling,
        async lazy() {
          const component = await import('../features/bidBoards/components/PresortSellingBoard');
          return { Component: component.PresortSellingBoard };
        }
      }
    ]
  });

  return routes;
}
